/**
 * Exploit Direction — bank-style, 21 prompts.
 */

import { Brain } from "@/shared/components/icons";
import { QuizLayout } from "@/features/quiz/components/QuizLayout";
import { QuizOption } from "@/features/quiz/components/QuizOption";
import { RevealPanel } from "@/features/quiz/components/RevealPanel";
import { useQuizSession } from "@/features/quiz/lib/useQuizSession";
import {
  EXPLOIT_DIRECTION_BANK,
  type Direction,
  type ExploitDirectionQuestion,
} from "@/features/quiz/lib/exploitDirectionQuiz";

const OPTIONS: Direction[] = ["more", "less", "same"];
const LABELS: Record<Direction, string> = {
  more: "More",
  less: "Less",
  same: "Same as baseline",
};

export function QuizExploitDirection() {
  const {
    question,
    revealedAnswer,
    isCorrect,
    correctAnswer,
    stats,
    submit,
    next,
    bankPosition,
    bankSize,
    isComplete,
    restart,
  } = useQuizSession<ExploitDirectionQuestion, Direction>({
    quizType: "exploit_direction",
    bank: EXPLOIT_DIRECTION_BANK,
    getCorrectAnswer: (q) => q.answer,
    getQuestionPayload: (q) => ({ prompt: q.prompt }),
  });

  return (
    <QuizLayout
      Icon={Brain}
      title="Exploit Direction"
      subtitle="Archetype → adjustment direction"
      description="Tests the direction of each per-archetype adjustment from sheets 8–14. Knowing the right direction is the bare minimum of exploit play: getting the sign wrong (bluffing more vs a station, less vs a nit) is worse than no exploit at all."
      stats={stats}
      bankPosition={bankPosition}
      bankSize={bankSize}
      isComplete={isComplete}
      onRestart={restart}
    >
      <p className="text-center text-text text-base leading-relaxed">
        {question.prompt}
      </p>

      <div className="grid grid-cols-3 gap-3">
        {OPTIONS.map((opt) => (
          <QuizOption
            key={opt}
            value={opt}
            revealedAnswer={revealedAnswer}
            correctAnswer={correctAnswer}
            onSelect={submit}
          >
            <span className="text-sm">{LABELS[opt]}</span>
          </QuizOption>
        ))}
      </div>

      {revealedAnswer !== null && (
        <RevealPanel
          isCorrect={isCorrect}
          answerLine={
            <>
              Answer: <strong>{LABELS[correctAnswer]}</strong>
            </>
          }
          rationale={question.rationale}
          onNext={next}
        />
      )}
    </QuizLayout>
  );
}
